import { memo } from "react";

import Icon from "@/components/Icon/Icon";
import { cn } from "@/lib/cn";

type ScrollButtonProps = {
  /** スクロールの方向 */
  direction: "left" | "right";
  /** クリックハンドラー */
  onClick: () => void;
  /** 無効状態 */
  disabled?: boolean;
  /**
   * 背景色のバリアント
   * - fill: `Neutral/Fill`
   * - bottomFill: `Neutral/Bottom Fill`
   */
  variant?: "fill" | "bottomFill";
  /** カスタムクラス名 */
  className?: string;
};

/**
 * スクロールボタン（シェブロン）
 *
 * 水平スクロール可能なUIで左右スクロールを行うボタン。
 * disabledの場合はボタン自体を表示しない。
 */
export const ScrollButton = memo(function ScrollButton({
  direction,
  onClick,
  disabled,
  variant = "fill",
  className,
}: ScrollButtonProps) {
  // disabledの場合は何も表示しない
  if (disabled) return null;

  // 左右の方向を判定
  const isLeft = direction === "left";

  // アイコン名を方向に基づいて決定
  const iconName = isLeft ? "ChevronLeft" : "ChevronRight";

  // アクセシビリティラベルを方向に基づいて決定
  const ariaLabel = isLeft ? "Scroll left" : "Scroll right";

  const backgroundClassName =
    variant === "bottomFill" ? "bg-neutral-bottom-fill" : "bg-neutral-fill";

  return (
    <div
      className={cn(
        "absolute z-10 flex items-center pointer-events-none h-9 w-12",
        isLeft ? "left-0 justify-start" : "right-0 justify-end",
        className,
      )}
    >
      {/* 影だけ上下をクリップする（focus ring はクリップしない） */}
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
        <div
          className={cn(
            "absolute top-0 size-9 pointer-events-none",
            isLeft
              ? "left-0 shadow-[4px_0px_4px_0px_rgba(0,0,0,0.1)]"
              : "right-0 shadow-[-4px_0px_4px_0px_rgba(0,0,0,0.1)]",
          )}
        />
      </div>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={onClick}
        className={cn(
          "pointer-events-auto relative flex items-center justify-center p-2 size-9 border-0 rounded-none",
          backgroundClassName,
          "text-neutral-muted-on-fill",
          "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring",
          "transition-colors hover:bg-neutral-fill-hovered",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "absolute top-0 h-full w-px bg-neutral-muted-border",
            isLeft ? "right-0" : "left-0",
          )}
        />
        <Icon iconName={iconName} size={20} inheritColor />
      </button>
    </div>
  );
});
